import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useContext,
} from "react";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ExclamationCircleOutlined,
  SearchOutlined,
  StopOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import useFetch from "../../../hooks/useFetch";
import { DATA_REFRESH_RATE } from "../../../configs/configs";
import AcknowledgeAlarmForm from "../../../components/common/AcknowledgeAlarmForm";
import { render } from "@antv/g2";
import { IndexContext } from "../../../context/IndexContext";
import TextArea from "antd/es/input/TextArea";
import { useForm } from "antd/es/form/Form";
import AlarmDetailForm from "../../alarm/AlarmDetailForm";

const { Title, Text } = Typography;

// function AlarmDetail({ alarm, form }) {
//   return (
//     <>
//       <Divider className="mt-1 mb-3" />
//       <Form form={form} layout="vertical" initialValues={alarm} clearOnDestroy>
//         <Row gutter={[18, 0]}>
//           <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
//             <Form.Item name="start" label="Start Datetime">
//               <Input readOnly></Input>
//             </Form.Item>
//           </Col>
//           <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
//             <Form.Item name="end" label="End Datetime">
//               <Input readOnly placeholder="-"></Input>
//             </Form.Item>
//           </Col>
//           <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
//             <Form.Item
//               name="parameter-name"
//               initialValue={`${alarm.parameter_group} - ${alarm.parameter}`}
//               label="Parameter"
//             >
//               <Input readOnly></Input>
//             </Form.Item>
//           </Col>
//           <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
//             <Form.Item name="level" label="Level">
//               <Select
//                 readOnly
//                 labelRender={({ value, label }) => {
//                   if (value == "Warning")
//                     return (
//                       <Tag color="var(--ant-color-warning)">
//                         <ExclamationCircleOutlined></ExclamationCircleOutlined>
//                         <Text style={{ color: "white" }}>{value}</Text>
//                       </Tag>
//                     );
//                   else if (value == "Error")
//                     return (
//                       <Tag color="var(--ant-color-error)">
//                         <StopOutlined></StopOutlined>
//                         <Text style={{ color: "white" }}>{value}</Text>
//                       </Tag>
//                     );
//                   else return <>{value}</>;
//                 }}
//                 dropdownRender={() => null}
//               ></Select>
//             </Form.Item>
//           </Col>
//           <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
//             <Form.Item name="value" label="Value">
//               <Input readOnly></Input>
//             </Form.Item>
//           </Col>
//           <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
//             <Form.Item label="Set Point">
//               <Space.Compact style={{ width: "100%" }}>
//                 <Form.Item name="operator" noStyle>
//                   <Input readOnly></Input>
//                 </Form.Item>
//                 <Form.Item name="setpoint" noStyle>
//                   <Input readOnly></Input>
//                 </Form.Item>
//               </Space.Compact>
//             </Form.Item>
//           </Col>
//           <Col span={24}>
//             <Form.Item name="message" label="Message">
//               <TextArea
//                 readOnly
//                 autoSize={{ minRows: 2, maxRows: 3 }}
//               ></TextArea>
//             </Form.Item>
//           </Col>
//           <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
//             <Form.Item name="acknowledged_by" label="Ackonwledged By">
//               <Input readOnly placeholder="-"></Input>
//             </Form.Item>
//           </Col>
//           <Col xs={24} sm={24} md={24} lg={12} xl={12} xxl={12}>
//             <Form.Item name="acknowledged_datetime" label="Ackonwledged On">
//               <Input readOnly placeholder="-"></Input>
//             </Form.Item>
//           </Col>
//           <Col span={24}>
//             <Form.Item name="remark" label="Remark" disabled={false}>
//               <TextArea
//                 maxLength={150}
//                 autoSize={{ minRows: 3, maxRows: 5 }}
//               ></TextArea>
//             </Form.Item>
//           </Col>
//         </Row>
//       </Form>
//     </>
//   );
// }

export default function AlarmTab({
  themeToken,
  tableWrapperViewportHeight = 1,
  deviceID,
  siteCommissioningDate,
}) {
  const { modalApi, notificationApi } = useContext(IndexContext);
  const timerRef = useRef(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [acknowledgeMode, setAcknowledgeMode] = useState("single");
  const [selectedAlarmView, setSelectedAlarmView] = useState("Active");
  const [filter, setFilter] = useState({
    startDate: dayjs().hour(0).minute(0).second(0).add(-14, "day"),
    endDate: dayjs().hour(0).minute(0).second(0).add(1, "day"),
  });

  const [startDate, setStartDate] = useState(
    dayjs(
      Math.max(
        dayjs().hour(0).minute(0).second(0).add(-14, "day").valueOf(),
        dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf()
      )
    )
  );
  const [endDate, setEndDate] = useState(
    dayjs().hour(0).minute(0).second(0).add(1, "day")
  );

  const [form] = useForm();

  // to check if undo button is clicked
  // if undo button is clicked, need to call api to get historical alarm data
  // cannot straight away call the api when undo button onClicked as startDate and endDate states are not changed during the api call
  const [isUndoClicked, setIsUndoClicked] = useState(false);

  const [fetchWrapper] = useFetch();
  const [firstLoadActiveAlarm, setFirstLoadActiveAlarm] = useState(true);
  const [firstLoadHistoricalAlarm, setFirstLoadHistoricalAlarm] =
    useState(true);

  // data for active alarm table
  const [activeAlarmData, setActiveAlarmData] = useState([]);
  // data for historical alarm table
  const [historicalAlarmData, setHistoricalAlarmData] = useState([]);

  // to open / close acknowledgement modal
  const [isAcknowledgementModalOpen, setIsAcknowledgementModalOpen] =
    useState(false);

  // alarm id if user clicks on the acknowledge button without checking the checkbox
  // (if user clicks the checkbox then acknowledge button, the alarm id(s) will be the selectedRowKeys)
  const [selectedAlarmID, setSelectedAlarmID] = useState(null);

  // for resizeable table
  const [tableHeight, setTableHeight] = useState(600);
  const filterRef = useRef();

  function resizeTable() {
    const node = filterRef.current;
    if (!node) {
      return;
    }
    const { height } = node.getBoundingClientRect();

    setTableHeight(
      window.innerHeight * tableWrapperViewportHeight - height - 285
    );
  }

  /* API to get device active alarm */
  function getSelectedDeviceActiveAlarm() {
    fetchWrapper({
      endpoint_url: "device_monitoring/getSelectedDeviceActiveAlarm",
      body: {
        device_id: deviceID,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setActiveAlarmData(fetch_output.active_alarm);
        }
        setFirstLoadActiveAlarm(false);
      },
    });
  }

  /* API to get device historical alarm */
  function getSelectedDeviceHistoricalAlarm() {
    fetchWrapper({
      endpoint_url: "device_monitoring/getSelectedDeviceHistoricalAlarm",
      body: {
        device_id: deviceID,
        start_date: startDate.format("YYYY-MM-DD HH:mm:ss"),
        end_date: endDate.format("YYYY-MM-DD HH:mm:ss"),
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setHistoricalAlarmData(fetch_output.historical_alarm);
        }
        setFirstLoadHistoricalAlarm(false);
      },
    });
  }

  function acknowledgeAlarm(id, remark, modal) {
    fetchWrapper({
      endpoint_url: "device_monitoring/acknowledgeAlarm",
      body: {
        alarm_ids: id,
        acknowledge_mark: remark,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setSelectedRowKeys([]);
          if (selectedAlarmView === "Active") {
            getSelectedDeviceActiveAlarm();
          } else {
            getSelectedDeviceHistoricalAlarm();
          }
          modal.destroy();
        } else {
          notificationApi.error({
            message: fetch_output.error,
          });
        }
      },
    });
  }

  function onRowClicked(row) {
    const modal = modalApi.confirm();

    modal.update({
      icon: null,
      title: "Alarm Detail",
      width: 600,
      style: { maxWidth: "90vw" },
      centered: true,
      content: <AlarmDetailForm alarm={row} form={form} />,
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          {row.acknowledged ? (
            <Button
              form="myForm"
              onClick={() =>
                acknowledgeAlarm(row.ID, form.getFieldValue("remark"), modal)
              }
              type="primary"
            >
              Save
            </Button>
          ) : (
            <Button
              form="myForm"
              onClick={() =>
                acknowledgeAlarm(row.ID, form.getFieldValue("remark"), modal)
              }
              type="primary"
            >
              Acknowledge
            </Button>
          )}
        </>
      ),
    });
  }

  useEffect(() => {
    if (selectedAlarmView === "Active") {
      if (deviceID) {
        getSelectedDeviceActiveAlarm();
      }

      // refresh data every configurable second
      timerRef.current = setInterval(() => {
        getSelectedDeviceActiveAlarm();
      }, DATA_REFRESH_RATE * 1000);
    }

    if (selectedAlarmView === "Historical") {
      if (deviceID) {
        getSelectedDeviceHistoricalAlarm();
      }
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }, [deviceID, selectedAlarmView]);

  useEffect(() => {
    if (isUndoClicked) {
      setIsUndoClicked(false);
      getSelectedDeviceHistoricalAlarm();
    }
  }, [isUndoClicked, startDate, endDate]);

  useLayoutEffect(() => {
    resizeTable();
    window.addEventListener("resize", resizeTable);

    return () => {
      window.removeEventListener("resize", resizeTable);
    };
  }, [filterRef]);

  useEffect(() => {
    resizeTable();
  }, [selectedAlarmView]);

  const columns = [
    {
      title: "Start",
      dataIndex: "start",
      ellipsis: true,
      width: 130,
      fixed: "left",
      sorter: (a, b) => {
        // Convert date strings to Date objects
        const dateA = new Date(a.start);
        const dateB = new Date(b.start);
        return dateA - dateB;
      },
    },
    {
      title: "End",
      dataIndex: "end",
      ellipsis: true,
      hidden: selectedAlarmView == "Active",
      width: 130,
      render: (end, record) => (!end ? "-" : end),
    },
    {
      title: "Parameter",
      dataIndex: "parameter",
      width: 220,
      ellipsis: true,
      render: (_, record) =>
        `${record["parameter_group"]} - ${record["parameter"]}`,
    },
    {
      title: "Value",
      dataIndex: "value",
      width: 80,
      render: (value, record) => value.toLocaleString(),
    },
    {
      title: "Level",
      dataIndex: "level",
      ellipsis: true,
      width: 100,
      filters: [
        {
          text: "Error",
          value: "ERROR",
        },
        {
          text: "Warning",
          value: "WARNING",
        },
      ],
      onFilter: (value, record) =>
        record.level.toUpperCase().indexOf(value.toUpperCase()) === 0,
      render: (_, record) => {
        return (
          <Tag
            color={
              _.toUpperCase() == "ERROR"
                ? themeToken.colorError
                : _.toUpperCase() == "WARNING"
                ? themeToken.colorWarning
                : themeToken.colorPrimary
            }
          >
            <Text style={{ color: "white" }}>{_}</Text>
          </Tag>
        );
      },
    },
    { title: "Message", dataIndex: "message" },
    { title: "Acknowledged By", dataIndex: "acknowledged_by", width: 160 },
    {
      title: "Acknowledged On",
      dataIndex: "acknowledged_datetime",
      width: 160,
    },
    {
      title: (
        <Button
          size="small"
          type="link"
          disabled={selectedRowKeys?.length == 0}
          onClick={() => {
            setAcknowledgeMode("multi");
            setIsAcknowledgementModalOpen(true);
          }}
        >
          Acknowledge ({selectedRowKeys?.length})
        </Button>
      ),
      key: "action",
      width: 150,
      render: (_, record) => (
        <Button
          size="small"
          type="link"
          disabled={record.acknowledged}
          onClick={() => {
            setAcknowledgeMode("single");
            setSelectedAlarmID(record.ID);
            setIsAcknowledgementModalOpen(true);
          }}
        >
          Acknowledge
        </Button>
      ),
    },
  ];

  return (
    <div
      style={{
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Flex justify="space-between" align="center" className="mb-2">
        <Title level={5} className="m-0">
          {selectedAlarmView} Alarm
        </Title>
        <Segmented
          options={["Active", "Historical"]}
          defaultValue={selectedAlarmView}
          onChange={(value) => {
            setSelectedAlarmView(value);
            setFirstLoadActiveAlarm(true);
            setFirstLoadHistoricalAlarm(true);
          }}
        ></Segmented>
      </Flex>

      <div ref={filterRef}>
        {selectedAlarmView == "Historical" ? (
          <Row gutter={[24, 24]} className="mt-5 mb-2">
            <Col
              xs={{ flex: "100%" }}
              sm={{ flex: "80%" }}
              md={{ flex: "70%" }}
              lg={{ flex: "50%" }}
              xl={{ flex: "50%" }}
              xxl={{ flex: "50%" }}
            >
              <Space.Compact block>
                <Button disabled>Start</Button>
                <DatePicker
                  style={{ width: "100%" }}
                  showTime
                  showHour
                  showMinute
                  showSecond={false}
                  allowClear={false}
                  value={startDate}
                  placement="bottomLeft"
                  needConfirm={false}
                  defaultValue={startDate}
                  onChange={(date) => {
                    if (date > endDate) {
                      setStartDate(endDate);
                      setEndDate(date);
                    } else setStartDate(date);
                  }}
                  maxDate={endDate}
                  minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
                />
                <Button disabled>End</Button>
                <DatePicker
                  style={{ width: "100%" }}
                  showTime
                  showHour
                  showMinute
                  showSecond={false}
                  allowClear={false}
                  value={endDate}
                  defaultValue={endDate}
                  needConfirm={false}
                  onChange={(date) => {
                    if (date < startDate) {
                      setEndDate(startDate);
                      setStartDate(date);
                    } else setEndDate(date);
                  }}
                  minDate={startDate}
                  placement="topLeft"
                />
                <Tooltip title="Reset to default date">
                  <Button
                    icon={
                      <UndoOutlined
                        onClick={() => {
                          setStartDate(
                            dayjs(
                              Math.max(
                                dayjs()
                                  .hour(0)
                                  .minute(0)
                                  .second(0)
                                  .add(-14, "day")
                                  .valueOf(),
                                dayjs(
                                  siteCommissioningDate,
                                  "YYYY-MM-DD"
                                ).valueOf()
                              )
                            )
                          );
                          setEndDate(
                            dayjs().hour(0).minute(0).second(0).add(1, "day")
                          );
                          setIsUndoClicked(true);
                        }}
                      />
                    }
                  />
                </Tooltip>
              </Space.Compact>
            </Col>
            <Col
              xs={{ flex: "100%" }}
              sm={{ flex: "20%" }}
              md={{ flex: "30%" }}
              lg={{ flex: "50%" }}
              xl={{ flex: "50%" }}
              xxl={{ flex: "50%" }}
            >
              <Flex justify="start" gap={12}>
                <Tooltip title="Search">
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={() => getSelectedDeviceHistoricalAlarm()}
                  >
                    Search
                  </Button>
                </Tooltip>
              </Flex>
            </Col>
          </Row>
        ) : (
          <></>
        )}
      </div>
      <Table
        onRow={(record, index) => {
          return {
            onDoubleClick: (evt) => {
              onRowClicked(record);
            },
          };
        }}
        loading={
          selectedAlarmView === "Active"
            ? firstLoadActiveAlarm
            : selectedAlarmView === "Historical"
            ? firstLoadHistoricalAlarm
            : false
        }
        sticky="true"
        size="small"
        showHeader
        rowSelection={{
          selectedRowKeys,
          onChange: (selectedRowKeys, selectedRows) =>
            setSelectedRowKeys(selectedRowKeys),
          getCheckboxProps: (record) => ({
            disabled: record.acknowledged,
          }),
        }}
        scroll={{ scrollToFirstRowOnChange: true, x: true, y: tableHeight }}
        pagination={{
          responsive: true,
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 20, 50, 100],
          position: ["bottomRight"],
          size: "small",
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        columns={columns}
        dataSource={
          selectedAlarmView === "Historical"
            ? historicalAlarmData
            : activeAlarmData
        }
      ></Table>
      <Modal
        centered
        open={isAcknowledgementModalOpen}
        title="Acknowledge Alarm"
        footer={false}
        style={{ width: "500px", maxWidth: "90vw" }}
        destroyOnClose={true}
        closable
        maskClosable={true}
        onCancel={() => setIsAcknowledgementModalOpen(false)}
      >
        <div>
          <AcknowledgeAlarmForm
            selectedAlarmIDs={
              acknowledgeMode == "single"
                ? selectedAlarmID
                : selectedRowKeys.length === 0
                ? selectedAlarmID
                : selectedRowKeys
            }
            setIsAcknowledgementModalOpen={setIsAcknowledgementModalOpen}
            setSelectedAlarmIDs={setSelectedRowKeys}
            selectedAlarmView={selectedAlarmView}
            getSelectedDeviceActiveAlarm={getSelectedDeviceActiveAlarm}
            getSelectedDeviceHistoricalAlarm={getSelectedDeviceHistoricalAlarm}
          />
        </div>
      </Modal>
    </div>
  );
}
